/**
 * The person's layout, kept in this browser (prototype v2, 2026-11-02). The vocabulary, the defaults and
 * every rule live in `layout-logic.ts`; this is only where the choice is held and written down.
 *
 * PER BROWSER, as the look is (`theme.ts`'s appearance store): a wall screen in the NOC and the same
 * engineer's laptop want different layouts, and neither is the organisation's to decide.
 *
 * THE SIDEBAR'S WIDTH STAYS `useNav`'s. The rail's collapse already has an owner (`theme.ts`), which
 * follows the window across the 768 px line; a second copy here would argue with it. `sideCollapsed`
 * in a patch is therefore handed to `useNav` and not stored.
 */
import { create } from 'zustand';
import { LAYOUT_DEFAULTS, sanitizeLayout, type LayoutSettings } from './layout-logic';
import { useNav } from './theme';

const KEY = 'lumen.layout.v1';

function read(): LayoutSettings {
  try {
    const raw = localStorage.getItem(KEY);
    return raw === null ? { ...LAYOUT_DEFAULTS } : sanitizeLayout(JSON.parse(raw));
  } catch {
    /* No storage (a private window, a test) or a record from another version: today's default. */
    return { ...LAYOUT_DEFAULTS };
  }
}

function write(s: LayoutSettings): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...s, sideCollapsed: undefined }));
  } catch {
    /* Storage full or refused: the choice holds for this visit, which is all a person can be told. */
  }
}

function narrow(): boolean {
  try {
    return window.matchMedia('(max-width: 767px)').matches;
  } catch {
    return false;
  }
}

export interface LayoutState extends LayoutSettings {
  /** Change some keys; the rest stay. */
  set: (patch: Partial<LayoutSettings>) => void;
  /** Back to today's JAZZ, every key. */
  reset: () => void;
}

export const useLayoutSettings = create<LayoutState>((setState, get) => ({
  ...read(),
  set: (patch) => {
    if (patch.sideCollapsed !== undefined) useNav.setState({ collapsed: patch.sideCollapsed });
    /* `sanitizeLayout` reads the known keys only, so the store's own functions fall away here. */
    const next = sanitizeLayout({ ...get(), ...patch });
    write(next);
    setState({ ...next, sideCollapsed: useNav.getState().collapsed });
  },
  reset: () => {
    /* The rail as it was: collapsed on a narrow window, open on a wide one (`useNav`'s own rule). */
    useNav.setState({ collapsed: narrow() });
    write({ ...LAYOUT_DEFAULTS });
    setState({ ...LAYOUT_DEFAULTS, sideCollapsed: useNav.getState().collapsed });
  },
}));
